import MessageBusTransport from '@central-products/message-transport-bus';

import { DataAccessServiceInterface } from '@central-products/interface-data-access-service';
import { AccountActionsInterface } from '@central-products/interface-account-actions';
import { SessionServiceInterface } from '@central-products/interface-session-service';
import { WelcomeMessagesFlowInterface } from '@central-products/interface-welcome-messages-flow';
import { WelcomeMessagesProviderInterface } from '@central-products/interface-welcome-messages-campaign-provider';
import { OnSiteMessagingComponentInterface } from '@central-products/interface-on-site-messaging-component';
import { OnSiteMessagingOrchestratorInterface } from '@central-products/interface-on-site-messaging-orchestrator';
import { feature as wmFlowFeature } from '@central-products/welcome-messages-flow';
import { feature as wmCampaignProviderFeature } from '@central-products/welcome-messages-campaign-provider';
import { feature as onSiteMessagingComponentFeature } from '@central-products/on-site-messaging-component';
import { feature as onSiteMessagingOrchestratorFeature } from '@central-products/on-site-messaging-orchestrator';
import {
  feature as onSiteMessagingTriggerLoginFeature,
  OnSiteMessagingTriggerLogin,
  canDisableTriggerLogin,
} from '@central-products/on-site-messaging-trigger-login';
import {
  WelcomeMessagesLegacyTrigger,
  feature as welcomeMessagesLegacyTriggerFeature,
} from '@central-products/welcome-messages-legacy-trigger';

declare global {
  interface Window {
    WH: {
      // tslint:disable-next-line:no-any
      messageBus: any;
      bootstrapper: {
        features: {
          'data-access-service': DataAccessServiceInterface;
          'account-actions': AccountActionsInterface;
          'session-service': SessionServiceInterface;
          'welcome-messages-flow': WelcomeMessagesFlowInterface;
          'welcome-messages-campaign-provider': WelcomeMessagesProviderInterface;
          'on-site-messaging-component': OnSiteMessagingComponentInterface;
          'on-site-messaging-orchestrator': OnSiteMessagingOrchestratorInterface;
          'on-site-messaging-trigger-login': typeof OnSiteMessagingTriggerLogin;
          'welcome-messages-legacy-trigger': typeof WelcomeMessagesLegacyTrigger;
        };
      };
      sportsbook?: {
        frontEndConfig: {
          overlayIntegrationEnabled: boolean;
        };
        lang?: string;
      };
    };
    consoleReference?: typeof console;
    docCookies?: {
      getItem(key: string): string | undefined;
    };
  }
}

export interface BootstrapperConfig {
  jurisdiction: string;
  environment: string;
  langCode: string;
  marketingPreferencePageUrl: string;
  optInTimeout: number;
  project: string;
  imageConversionReqTimeout: number;
  disableLoginTrigger: boolean;
}

export function bootstrap(
  {
    langCode,
    environment,
    jurisdiction,
    marketingPreferencePageUrl,
    optInTimeout,
    project,
    imageConversionReqTimeout,
    disableLoginTrigger,
  }: BootstrapperConfig,
  transport: MessageBusTransport,
) {
  const dependencies = window.WH.bootstrapper.features;
  // @ts-ignore - TODO: https://jira.willhillatlas.com/browse/SWM-372
  const wmFlowRole: 'welcome-messages-flow' = wmFlowFeature.role;
  // @ts-ignore
  const wmCampaignProviderRole: 'welcome-messages-campaign-provider' = wmCampaignProviderFeature.role;
  // @ts-ignore
  const onSiteMessagingOrchestratorRole: 'on-site-messaging-orchestrator' = onSiteMessagingOrchestratorFeature.role;
  // @ts-ignore - TODO: Refactor this package to new version of bootstrapper to not throw typescript errors
  const onSiteMessagingComponentRole: 'on-site-messaging-component' = onSiteMessagingComponentFeature.role;
  // @ts-ignore - TODO: Refactor this package to new version of bootstrapper to not throw typescript errors
  const onSiteMessagingTriggerLoginRole: 'on-site-messaging-trigger-login' = onSiteMessagingTriggerLoginFeature.role;
  // @ts-ignore - TODO: Refactor this package to new version of bootstrapper to not throw typescript errors
  const welcomeMessagesLegacyTriggerRole: 'welcome-messages-legacy-trigger' = welcomeMessagesLegacyTriggerFeature.role;

  const AccountActionsTransportInterface = transport.get<AccountActionsInterface>(AccountActionsInterface);
  const OnSiteMessagingOrchestratorTransportInterface = transport.get<OnSiteMessagingOrchestratorInterface>(
    OnSiteMessagingOrchestratorInterface,
  );
  const WmCampaignProviderTransportInterface = transport.get<WelcomeMessagesProviderInterface>(
    WelcomeMessagesProviderInterface,
  );
  const WelcomeMessagesFlowTransportInterface =
    transport.get<WelcomeMessagesFlowInterface>(WelcomeMessagesFlowInterface);
  const OnSiteMessagingComponentTransportInterface = transport.get<OnSiteMessagingComponentInterface>(
    OnSiteMessagingComponentInterface,
  );
  const SessionServiceTransportInterface = transport.get<SessionServiceInterface>(SessionServiceInterface);

  dependencies[wmCampaignProviderRole] = WmCampaignProviderTransportInterface;
  dependencies[wmFlowRole] = WelcomeMessagesFlowTransportInterface;
  dependencies[onSiteMessagingOrchestratorRole] = OnSiteMessagingOrchestratorTransportInterface;
  dependencies[onSiteMessagingComponentRole] = OnSiteMessagingComponentTransportInterface;
  // @ts-ignore
  const wmCampaignProviderInstance = wmCampaignProviderFeature.create(
    {},
    { 'data-access-service': dependencies['data-access-service'] },
    transport,
  );
  // @ts-ignore
  const onSiteMessagingOrchestratorInstance = onSiteMessagingOrchestratorFeature.create({}, {}, transport);
  // @ts-ignore
  const onSiteMessagingComponentInstance = onSiteMessagingComponentFeature.create(
    { project, environment, jurisdiction },
    {
      'data-access-service': dependencies['data-access-service'],
      'on-site-messaging-orchestrator': OnSiteMessagingOrchestratorTransportInterface,
      'welcome-messages-campaign-provider': WmCampaignProviderTransportInterface,
    },
    transport,
  );
  // @ts-ignore
  const wmFlowInstance = wmFlowFeature.create(
    { environment, jurisdiction, langCode, marketingPreferencePageUrl, optInTimeout, imageConversionReqTimeout },
    {
      'account-actions': AccountActionsTransportInterface,
      'welcome-messages-campaign-provider': WmCampaignProviderTransportInterface,
      'on-site-messaging-orchestrator': OnSiteMessagingOrchestratorTransportInterface,
    },
    transport,
  );

  const loginTriggerDisabled = canDisableTriggerLogin(disableLoginTrigger);
  if (!loginTriggerDisabled) {
    // @ts-ignore
    dependencies[onSiteMessagingTriggerLoginRole] = onSiteMessagingTriggerLoginFeature.create(
      { disableLogin: disableLoginTrigger },
      {
        'session-service': SessionServiceTransportInterface,
        'on-site-messaging-component': OnSiteMessagingComponentTransportInterface,
      },
      transport,
    );
  }

  // @ts-ignore https://jira.willhillatlas.com/browse/SWM-372
  dependencies[welcomeMessagesLegacyTriggerRole] = welcomeMessagesLegacyTriggerFeature.create(
    {},
    {
      'account-actions': AccountActionsTransportInterface,
      'on-site-messaging-component': OnSiteMessagingComponentTransportInterface,
    },
    transport,
  );

  transport.bind(wmCampaignProviderInstance);
  transport.bind(onSiteMessagingOrchestratorInstance);
  transport.bind(onSiteMessagingComponentInstance);
  transport.bind(wmFlowInstance);

  return () => {
    // @ts-ignore
    wmFlowFeature.init(wmFlowInstance);
    // @ts-ignore
    onSiteMessagingComponentFeature.init(onSiteMessagingComponentInstance);
    // @ts-ignore https://jira.willhillatlas.com/browse/SWM-372
    welcomeMessagesLegacyTriggerFeature.init(dependencies[welcomeMessagesLegacyTriggerRole]);

    if (!loginTriggerDisabled) {
      // @ts-ignore
      onSiteMessagingTriggerLoginFeature.init(dependencies[onSiteMessagingTriggerLoginRole]);
    }
  };
}
